import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ScreenLink as Link } from './permissions/ScreenLink';
import { usePermissions } from '../hooks/usePermissions';
import { isPortalIdentity } from './permissions/screenAccess';
import { BellRing, CheckCheck, CheckCircle2, Inbox, Loader2, Plus, Search, Settings2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import ReminderFormDialog from './reminders/ReminderFormDialog';
import MyReminders from './reminders/MyReminders';
import { isReminder, type ReminderRow } from '../utils/reminderModel';
import { api } from '../services/api';
import type { Notification } from '../types';
import Dialog from './ui/Dialog';
import Tabs from './ui/Tabs';
import StateBlock from './ui/StateBlock';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from './TimerSummaryModal';
import {
  feedStatusOf, filterByTab, groupByDay, isCapped, isUnread, matchingEventTypes, mergeFeedPages, nextFeedCursor,
  normalizeSearch, notificationTarget, searchFeedParams, NOTIFICATION_SEARCH_DEBOUNCE_MS,
  NOTIFICATION_SEARCH_MAX, type NotificationTab,
} from './notificationCenterModel';
import NotificationRow from './NotificationRow';
import HelpTip from './ui/HelpTip';
import { useStartTimer } from './home/useHomeData';

/** The bell's query: the same key and call everywhere, so the badge and the drawer agree. */
export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

/**
 * FIX-NOTIF N-4: the portal bell of a customer with several organizations may be narrowed to the
 * one chosen in the portal. Still under the `['notifications']` prefix, so every invalidation hits it.
 */
export function notificationsKey(organizationId?: string) {
  return organizationId ? ([...NOTIFICATIONS_QUERY_KEY, { organization_id: organizationId }] as const) : NOTIFICATIONS_QUERY_KEY;
}

/**
 * NotificationCenter (T-48, PRD §4.20): a drawer from the inline-end edge,
 * opened by the bell.
 *
 * - Tabs "הכול · לא נקראו · אישורים" (B1, NL-06). "הכול" and "אישורים" read
 *   the full feed (`GET /notifications?status=all`, read history included);
 *   "לא נקראו" reads `?status=unread`. Pages of 50, newest first, and
 *   "טעינת קודמות" at the end loads the next page by time. "אישורים" narrows
 *   the loaded rows to approval requests (no server filter).
 * - The feed is fetched only while the drawer is open. The bell, its badge
 *   and its polling keep the unchanged `GET /notifications` (unread, 50), which
 *   also gives the "N לא נקראו" line and the unread tab count here.
 * - Day groups: היום · אתמול · השבוע · קודם. An unread row is marked with a dot.
 * - A row opens its deep link and marks it read (`PATCH /notifications/:id/read`);
 *   "סימון הכול כנקרא" calls `POST /notifications/read-all`. Both refresh every
 *   query under the shared key, so the badge, the chat dock and this list stay
 *   in step. "אזכורים" has no event type yet (needs logic).
 * - P-30b (D-173): a search field. The text is searched ON THE SERVER
 *   (`?q=`, debounced), so old notifications that were never loaded are found
 *   too; the event types whose label (as shown here) matches go along. The
 *   tabs, paging, read marks and links work the same on the results.
 */
export default function NotificationCenter({ open, onClose, organizationId, onOpenPreferences }: {
  open: boolean;
  onClose: () => void;
  /** FIX-NOTIF N-4 - narrow to one organization (portal, several organizations). */
  organizationId?: string;
  /** FIX-NOTIF N-3 - the portal opens the preferences inside the portal (/profile is closed to a customer). */
  onOpenPreferences?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<NotificationTab>('all');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  // W-01 - "התראה חדשה" / "התראות שקבעתי": the form, and the list that replaces the feed while it is shown.
  const selfId = useAuthStore((s) => s.user?.id) ?? '';
  // Q-DQ-114: a customer's notification settings are in the portal's "הפרופיל שלי", not the internal shell.
  const prefsLink = useAuthStore((s) => isPortalIdentity(s.user)) ? '/portal?tab=profile&ptab=notifications' : '/profile?tab=notifications';
  const [view, setView] = useState<'feed' | 'mine'>('feed');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ReminderRow | null>(null);
  // Only OTE employees may set reminders: the server answers 403 to anyone else, and the buttons stay hidden.
  // A portal user is never asked (the 403 used to print a console error on every portal page).
  const { isOteEmployee } = usePermissions();
  const recipientsQuery = useQuery({
    queryKey: ['reminders', 'recipients'],
    queryFn: () => api.getReminderRecipients(),
    enabled: open && isOteEmployee === true,
    retry: false,
    staleTime: 60_000,
  });
  const recipients = recipientsQuery.data?.data ?? [];
  const canRemind = recipientsQuery.isSuccess && recipients.length > 0;
  useEffect(() => {
    const id = setTimeout(() => setSearch(normalizeSearch(searchText)), NOTIFICATION_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchText]);
  // A closed drawer forgets the search.
  useEffect(() => {
    if (!open) { setSearchText(''); setSearch(''); setView('feed'); }
  }, [open]);

  // The bell's query (same key and call): the unread count and "סימון הכול".
  const { data: unreadData } = useQuery({
    queryKey: notificationsKey(organizationId),
    queryFn: () => (organizationId ? api.getNotifications({ organization_id: organizationId }) : api.getNotifications()),
  });
  const unread: Notification[] = useMemo(() => unreadData?.data ?? [], [unreadData]);

  const status = feedStatusOf(tab);
  const searchTypes = useMemo(
    () => (search ? matchingEventTypes(search, (t('notifications', { returnObjects: true }) as Record<string, unknown>) || {}) : []),
    [search, t]
  );
  const feed = useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'feed', status, search, organizationId ?? ''],
    queryFn: ({ pageParam }) => api.getNotifications({ ...searchFeedParams(status, search, searchTypes, pageParam), ...(organizationId ? { organization_id: organizationId } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last, all) => nextFeedCursor<Notification>(last, all),
    enabled: open,
  });
  const { isPending, isError, error, refetch, isRefetching } = feed;
  const all: Notification[] = useMemo(() => mergeFeedPages<Notification>(feed.data?.pages ?? []), [feed.data]);
  const visible = filterByTab(all, tab, isOteEmployee === false ? 'portal' : 'staff');
  // Re-grouped on every render is cheap (a few pages of 50 rows).
  const groups = groupByDay(visible, new Date());

  const invalidate = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: invalidate,
    onError: () => toast('error', t('notifications.markReadError')),
  });

  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      invalidate();
      toast('success', t('notificationCenter.allMarked'));
    },
    onError: () => toast('error', t('notifications.markAllReadError')),
  });

  // W-01 - the recipient's "בוצע" / "השתק" on one firing day.
  const ackReminder = useMutation({
    mutationFn: (v: { id: string; kind: 'done' | 'silence' }) => api.ackReminderOccurrence(v.id, v.kind),
    onSuccess: invalidate,
    onError: () => toast('error', t('reminder.ackFailed')),
  });

  const { canPath } = usePermissions();
  const openRow = (n: Notification) => {
    if (isUnread(n)) markRead.mutate(n.id);
    const target = notificationTarget(n);
    onClose();
    // A reminder has no screen to open (W-01); a closed screen does not exist for this person: the row is only marked read.
    if (!isReminder(n) && target && canPath(target)) navigate(target);
  };

  // FIX-D300-S2 (D-300 sec.5) — "התחלת עבודה" on the manager's decision about a customer with no coverage.
  const startCoverageWork = useStartTimer();

  // P-37 (D-194 Q-184) — "הטיימר על X רץ כבר 8 שעות. עדיין עובדת?" [כן] [עצירה].
  const stopLongTimer = useMutation({
    mutationFn: (n: Notification) => api.stopTimer(String(n.metadata?.timer_id ?? '')),
    onSuccess: (_r, n) => {
      if (isUnread(n)) markRead.mutate(n.id);
      toast('success', t('replyTimer.longRun.stopped'));
    },
    onError: (err) => toast('error', apiErrorMessage(err) || t('replyTimer.longRun.stopFailed')),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
    },
  });

  const footer = (
    <div className="flex w-full items-center justify-between gap-3">
      {onOpenPreferences ? (
        <button
          type="button"
          onClick={() => { onClose(); onOpenPreferences(); }}
          className="inline-flex items-center gap-1.5 rounded-md text-sm text-text-2 hover:text-text"
        >
          <Settings2 size={16} strokeWidth={1.75} aria-hidden="true" />
          {t('notificationCenter.preferences')}
        </button>
      ) : (
        <Link
          to={prefsLink}
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-md text-sm text-text-2 hover:text-text"
        >
          <Settings2 size={16} strokeWidth={1.75} aria-hidden="true" />
          {t('notificationCenter.preferences')}
        </Link>
      )}
      {unread.length > 0 && (
        <button
          type="button"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending}
          aria-busy={markAll.isPending || undefined}
          className="btn btn-secondary btn-sm"
        >
          {markAll.isPending
            ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            : <CheckCheck size={14} strokeWidth={1.75} aria-hidden="true" />}
          {t('notificationCenter.markAllRead')}
        </button>
      )}
    </div>
  );

  // From the bell's unread list (at most 50): an exact count, or "the latest 50".
  const description = !unreadData || unread.length === 0
    ? undefined
    : isCapped(unread.length)
      ? t('notificationCenter.cappedLine', { count: unread.length })
      : t('notificationCenter.unreadLine', { count: unread.length });

  const empty = search
    ? { icon: Search, title: t('notificationCenter.searchEmptyTitle'), line: t('notificationCenter.searchEmptyLine', { text: search }) }
    : tab === 'unread'
    ? { icon: CheckCircle2, title: t('notificationCenter.emptyTitle'), line: t('notificationCenter.emptyLine') }
    : tab === 'approvals'
      ? { icon: CheckCircle2, title: t('notificationCenter.emptyApprovalsTitle'), line: feed.hasNextPage ? t('notificationCenter.emptyApprovalsMore') : t('notificationCenter.emptyApprovalsLine') }
      : { icon: Inbox, title: t('notificationCenter.emptyAllTitle'), line: t('notificationCenter.emptyAllLine') };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="drawer"
      title={t('notificationCenter.title')}
      description={description}
      footer={footer}
      bodyClassName="px-0 pb-4"
      panelProps={{ 'data-notification-center': '' }}
    >
      <div className="px-5 pb-3">
        {canRemind && (
          <div className="mb-3 flex flex-wrap items-center gap-2" data-reminder-actions="">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
              <Plus size={14} strokeWidth={1.75} aria-hidden="true" />
              {t('reminder.newButton')}
            </button>
            <HelpTip title={t('reminder.newButton')} body={t('reminder.newHelp')} />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              aria-pressed={view === 'mine'}
              onClick={() => setView((v) => (v === 'mine' ? 'feed' : 'mine'))}
            >
              <BellRing size={14} strokeWidth={1.75} aria-hidden="true" />
              {view === 'mine' ? t('reminder.backToFeed') : t('reminder.mineButton')}
            </button>
          </div>
        )}
        <Tabs
          variant="segmented"
          ariaLabel={t('notificationCenter.filterLabel')}
          value={tab}
          onChange={setTab}
          items={[
            { key: 'all', label: t('notificationCenter.tabAll') },
            { key: 'unread', label: t('notificationCenter.tabUnread'), count: unreadData && !isCapped(unread.length) ? unread.length : undefined },
            { key: 'approvals', label: t('notificationCenter.tabApprovals') },
          ]}
        />
        <div className="relative mt-3">
          <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-text-3" aria-hidden="true" />
          <input
            type="search"
            value={searchText}
            maxLength={NOTIFICATION_SEARCH_MAX}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('notificationCenter.searchPlaceholder')}
            aria-label={t('notificationCenter.searchPlaceholder')}
            className="input text-sm w-full ps-9"
            data-notification-search=""
          />
        </div>
      </div>

      {view === 'mine' ? (
        <MyReminders selfId={selfId} onEdit={(r) => { setEditing(r); setFormOpen(true); }} />
      ) : isPending ? (
        <div className="px-5"><StateBlock kind="loading" size="panel" rows={5} /></div>
      ) : isError ? (
        <StateBlock
          kind="error"
          size="panel"
          title={t('notificationCenter.loadError')}
          error={error}
          onRetry={() => { void refetch(); }}
          retrying={isRefetching}
        />
      ) : visible.length === 0 ? (
        <StateBlock
          kind="empty"
          size="panel"
          icon={empty.icon}
          title={empty.title}
          description={empty.line}
        />
      ) : (
        <div className="space-y-4">
          {groups.map(({ group, items }) => (
            <section key={group} aria-labelledby={`nc-group-${group}`}>
              <h3 id={`nc-group-${group}`} className="px-5 pb-1 text-xs font-medium text-text-2">
                {t(`notificationCenter.group.${group}`)}
              </h3>
              <ul>
                {items.map((n) => {
                  const fresh = isUnread(n);
                  return (
                    <li key={n.id}>
                      <NotificationRow n={n} onOpen={() => openRow(n)} />
                      {isReminder(n) && typeof n.metadata?.occurrence_id === 'string' && (fresh || n.metadata?.keep_until_done === true) && (
                        <span className="flex gap-2 pb-2.5 pe-5 ps-7" data-testid="reminder-actions">
                          <button type="button" className="btn btn-secondary btn-sm" disabled={ackReminder.isPending} onClick={() => ackReminder.mutate({ id: String(n.metadata?.occurrence_id), kind: 'done' })}>
                            {t('reminder.done')}
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={ackReminder.isPending} onClick={() => ackReminder.mutate({ id: String(n.metadata?.occurrence_id), kind: 'silence' })}>
                            {t('reminder.silence')}
                          </button>
                        </span>
                      )}
                      {n.event_type === 'customer_needs_account_manager' && typeof n.metadata?.organization_id === 'string' && (
                        <span className="flex gap-2 pb-2.5 pe-5 ps-7" data-testid="needs-am-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              if (isUnread(n)) markRead.mutate(n.id);
                              onClose();
                              navigate(`/organizations/${String(n.metadata?.organization_id)}`);
                            }}
                          >
                            {t('notifications.customerNeedsAmAppoint')}
                          </button>
                        </span>
                      )}
                      {n.event_type === 'warranty_ending' && typeof n.metadata?.organization_id === 'string' && (
                        <span className="flex gap-2 pb-2.5 pe-5 ps-7" data-testid="warranty-ending-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              if (isUnread(n)) markRead.mutate(n.id);
                              onClose();
                              navigate(`/organizations/${String(n.metadata?.organization_id)}?new_engagement=1`);
                            }}
                          >
                            {t('notifications.warrantyNewEngagement')}
                          </button>
                        </span>
                      )}
                      {/* FIX-D300-S2 (D-300 sec.5) — the manager decided: "התחלת עבודה" straight from the notice
                          (the regular timer flow: the engagement question, "עצירה ומעבר / שניהם", PRESERVE #1). */}
                      {n.event_type === 'coverage_request_resolved' && typeof n.metadata?.ticket_id === 'string' && (
                        <span className="flex gap-2 pb-2.5 pe-5 ps-7" data-testid="coverage-resolved-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={startCoverageWork.isPending}
                            onClick={() => {
                              if (isUnread(n)) markRead.mutate(n.id);
                              onClose();
                              startCoverageWork.mutate({ id: String(n.metadata?.ticket_id), title: String(n.metadata?.ticket_title ?? '') });
                            }}
                          >
                            {t('coverage.startWork')}
                          </button>
                        </span>
                      )}
                      {n.event_type === 'timer_long_running' && typeof n.metadata?.timer_id === 'string' && (
                        <span className="flex gap-2 pb-2.5 pe-5 ps-7" data-testid="long-run-actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { if (isUnread(n)) markRead.mutate(n.id); }}>
                            {t('replyTimer.longRun.yes')}
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={stopLongTimer.isPending} onClick={() => stopLongTimer.mutate(n)}>
                            {t('replyTimer.longRun.stop')}
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {view === 'feed' && !isPending && !isError && feed.hasNextPage && (
        <div className="flex flex-col items-center gap-2 px-5 pt-4">
          {feed.isFetchNextPageError && (
            <p role="alert" className="text-xs text-danger-700">{t('notificationCenter.loadMoreError')}</p>
          )}
          <button
            type="button"
            onClick={() => { void feed.fetchNextPage(); }}
            disabled={feed.isFetchingNextPage}
            aria-busy={feed.isFetchingNextPage || undefined}
            className="btn btn-secondary btn-sm"
          >
            {feed.isFetchingNextPage && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {t('notificationCenter.loadMore')}
          </button>
        </div>
      )}
      <ReminderFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        selfId={selfId}
        recipients={recipients}
        editing={editing}
      />
    </Dialog>
  );
}
